import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Wifi, Video, GraduationCap, Church, Bot, Rocket, Users, BookOpen, Star, Play,
  Heart, Globe, Mic, Laptop, Camera, Target, Shield, Lightbulb, Eye,
  Plus, ExternalLink, ArrowRight, Check,
} from "lucide-react";
import { normalizeImgUrl } from "@/lib/utils-img";

// Mapa nombre (string, tal como se guarda en el CMS) → componente de icono Lucide.
export const ICON_MAP = {
  Wifi, Video, GraduationCap, Church, Bot, Rocket, Users, BookOpen, Star, Play,
  Heart, Globe, Mic, Laptop, Camera, Target, Shield, Lightbulb, Eye,
};

/**
 * Tarjeta de servicio con panel de detalle deslizante.
 *
 * Antes era un volteo 3D en el eje Y de medio segundo con `ease-in-out`, que
 * es justo la curva más ajena al lenguaje de Apple —allí no hay nada que gire
 * sobre sí mismo—. Ahora el detalle sube desde abajo cubriendo la tarjeta: la
 * misma información, un gesto que sí encaja.
 *
 * El cambio arregla además un fallo real de accesibilidad. En la versión con
 * volteo, la cara oculta solo llevaba `pointer-events: none`, sin `inert` ni
 * `display:none`, así que el enlace «Visitar» seguía en el orden de tabulación
 * estando invisible: el foco del teclado desaparecía de la pantalla.
 *
 * `abierto` es la única fuente de verdad —lo activan el ratón, el teclado y el
 * toque por igual— para que el panel nunca esté visible pero inerte.
 *
 * `full`: variante a toda la sección (una tarjeta por slide, ver
 * `CarouselSection` `slideWidth="full"`). Mismo componente, solo escala la
 * tipografía y el icono para llenar el espacio de un slide en vez del de una
 * tarjeta entre varias.
 */
export default function ServiceFlipCard({ card, index, full = false }) {
  const [abierto, setAbierto] = useState(false);
  const raizRef = useRef(null);

  const Icon = ICON_MAP[card.icon] || Rocket;
  const image = normalizeImgUrl(card.image);
  const linkTarget = card.url || card.href;
  const linkLabel = card.cta || "Visitar";
  const isExternal = /^https?:\/\//.test(linkTarget || "");
  const features = Array.isArray(card.features) ? card.features.filter(Boolean) : [];
  const panelId = `servicio-panel-${index}`;

  // Al salir el foco de la tarjeta entera se cierra el panel. `relatedTarget`
  // es el elemento que recibe el foco: si sigue dentro, no hay que cerrar.
  const onBlurCapture = e => {
    if (!raizRef.current?.contains(e.relatedTarget)) setAbierto(false);
  };

  return (
    <div
      ref={raizRef}
      className={`service-card${full ? " service-card--full" : ""}`}
      data-testid={`service-card-${index}`}
      onMouseEnter={() => setAbierto(true)}
      onMouseLeave={() => setAbierto(false)}
      onBlurCapture={onBlurCapture}
    >
      {/* Cara visible */}
      <div
        className={`card-front${image ? " has-image" : ""}`}
        style={image ? { backgroundImage: `url(${image})` } : undefined}
      >
        <div
          className={`rounded-2xl flex items-center justify-center ${full ? "w-16 h-16 mb-7" : "w-12 h-12 mb-5"} ${
            image ? "bg-white/20 backdrop-blur-sm" : "bg-[#f5f5f7]"
          }`}
        >
          <Icon
            className={`${full ? "w-8 h-8" : "w-6 h-6"} ${image ? "text-white" : "text-foreground"}`}
            strokeWidth={1.5}
            aria-hidden="true"
          />
        </div>
        <h3
          className={`font-heading font-bold ${full ? "mb-3" : "text-lg mb-2"} ${image ? "text-white" : "text-foreground"}`}
          style={full ? { fontSize: "clamp(28px, 3vw, 40px)", letterSpacing: "-.02em" } : undefined}
        >
          {card.title}
        </h3>
        {card.subtitle && (
          <p
            className={`leading-relaxed ${full ? "text-lg md:text-xl max-w-xl" : "text-sm"} ${
              image ? "text-white/85" : "text-muted-foreground"
            }`}
          >
            {card.subtitle}
          </p>
        )}
      </div>

      {/* Control de apertura: cubre la cara visible sin taparla. Es lo que
          hace la tarjeta operable con teclado y con toque. */}
      <button
        type="button"
        className="card-toggle focus-ring"
        aria-expanded={abierto}
        aria-controls={panelId}
        onClick={() => setAbierto(a => !a)}
      >
        <span className="sr-only">
          {abierto ? `Ocultar detalles de ${card.title}` : `Ver detalles de ${card.title}`}
        </span>
        <span className={`card-hint${image ? " on-image" : ""}`} aria-hidden="true">
          <Plus className="w-3.5 h-3.5" />
        </span>
      </button>

      {/* Panel de detalle */}
      <div
        id={panelId}
        className={`card-panel${abierto ? " is-open" : ""}`}
        inert={!abierto}
      >
        {card.category && <span className="card-category">{card.category}</span>}
        <h3 className={`font-heading font-bold ${full ? "text-2xl md:text-3xl mb-3" : "text-lg mb-2"}`}>
          {card.title}
        </h3>
        <div className="card-panel-body">
          {card.desc && (
            <p className={`card-panel-desc${full ? " text-base md:text-lg max-w-2xl" : ""}`}>{card.desc}</p>
          )}
          {features.length > 0 && (
            <ul className={`card-panel-features${full ? " md:text-base" : ""}`}>
              {features.map((f, i) => (
                <li key={i}>
                  <Check className="w-4 h-4 flex-shrink-0" aria-hidden="true" /> {f}
                </li>
              ))}
            </ul>
          )}
        </div>
        {linkTarget && (
          isExternal ? (
            <a href={linkTarget} target="_blank" rel="noopener noreferrer" className="visit-btn focus-ring-light">
              {linkLabel} <ExternalLink className="w-4 h-4" aria-hidden="true" />
            </a>
          ) : (
            <Link to={linkTarget} className="visit-btn focus-ring-light">
              {linkLabel} <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          )
        )}
      </div>
    </div>
  );
}
